/**
 * Schools Service - API client for school records and academic terms
 */
import api from '../utils/api';

const schoolsService = {
  listSchools: async () => {
    try {
      return await api.get('/api/schools');
    } catch (error) {
      console.error('Error fetching schools:', error);
      throw error;
    }
  },

  getSchool: async (schoolId) => {
    try {
      return await api.get(`/api/schools/${schoolId}`);
    } catch (error) {
      console.error('Error fetching school:', error);
      throw error;
    }
  },

  createSchool: async (data) => {
    try {
      return await api.post('/api/schools', data);
    } catch (error) {
      console.error('Error creating school:', error);
      throw error;
    }
  },

  updateSchool: async (schoolId, data) => {
    try {
      return await api.put(`/api/schools/${schoolId}`, data);
    } catch (error) {
      console.error('Error updating school:', error);
      throw error;
    }
  },

  uploadLogo: async (schoolId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      return await api.post(`/api/schools/${schoolId}/logo`, formData);
    } catch (error) {
      console.error('Error uploading school logo:', error);
      throw error;
    }
  },

  listAllTerms: async () => {
    try {
      return await api.get('/api/schools/terms/list');
    } catch (error) {
      console.error('Error fetching terms list:', error);
      throw error;
    }
  },

  listTerms: async (schoolId) => {
    try {
      return await api.get(`/api/schools/${schoolId}/terms`);
    } catch (error) {
      console.error('Error fetching academic terms:', error);
      throw error;
    }
  },

  createTerm: async (schoolId, data) => {
    try {
      return await api.post(`/api/schools/${schoolId}/terms`, data);
    } catch (error) {
      console.error('Error creating academic term:', error);
      throw error;
    }
  },

  updateTerm: async (schoolId, termId, data) => {
    try {
      return await api.put(`/api/schools/${schoolId}/terms/${termId}`, data);
    } catch (error) {
      console.error('Error updating academic term:', error);
      throw error;
    }
  },

  getTermDeleteImpact: async (schoolId, termId) => {
    try {
      return await api.get(`/api/schools/${schoolId}/terms/${termId}/delete-impact`);
    } catch (error) {
      console.error('Error fetching term delete impact:', error);
      throw error;
    }
  },

  deleteTerm: async (schoolId, termId) => {
    try {
      return await api.delete(`/api/schools/${schoolId}/terms/${termId}`);
    } catch (error) {
      console.error('Error deleting academic term:', error);
      throw error;
    }
  },

  lockTerm: async (schoolId, termId) => {
    try {
      return await api.put(`/api/schools/${schoolId}/terms/${termId}/lock`);
    } catch (error) {
      console.error('Error locking academic term:', error);
      throw error;
    }
  },

  unlockTerm: async (schoolId, termId) => {
    try {
      return await api.put(`/api/schools/${schoolId}/terms/${termId}/unlock`);
    } catch (error) {
      console.error('Error unlocking academic term:', error);
      throw error;
    }
  },

  closeTerm: async (schoolId, termId) => {
    try {
      return await api.post(`/api/schools/${schoolId}/terms/${termId}/close`);
    } catch (error) {
      console.error('Error closing academic term:', error);
      throw error;
    }
  },

  listAcademicYears: async () => {
    return await api.get('/api/academic-calendar/years');
  },

  createAcademicYear: async (data) => {
    return await api.post('/api/academic-calendar/years', data);
  },

  updateAcademicYear: async (yearId, data) => {
    return await api.put(`/api/academic-calendar/years/${yearId}`, data);
  },

  listCalendarEvents: async (academicYearId) => {
    return await api.get('/api/academic-calendar/events', {
      params: academicYearId ? { academic_year_id: academicYearId } : {}
    });
  },

  createCalendarEvent: async (data) => {
    return await api.post('/api/academic-calendar/events', data);
  },

  updateCalendarEvent: async (eventId, data) => {
    return await api.put(`/api/academic-calendar/events/${eventId}`, data);
  },

  deleteCalendarEvent: async (eventId) => {
    return await api.delete(`/api/academic-calendar/events/${eventId}`);
  },

  previewRollover: async (data) => {
    return await api.post('/api/academic-calendar/rollover/preview', data);
  },

  applyRollover: async (data) => {
    return await api.post('/api/academic-calendar/rollover', data);
  },

  // ---- Calendar sync: .ics download + subscribable feed ----
  downloadCalendarIcs: async () => {
    return await api.get('/api/academic-calendar/export.ics', { raw: true });
  },

  getCalendarFeedToken: async () => {
    return await api.get('/api/academic-calendar/feed-token');
  },

  regenerateCalendarFeedToken: async () => {
    return await api.post('/api/academic-calendar/feed-token/regenerate');
  },

  revokeCalendarFeedToken: async () => {
    return await api.delete('/api/academic-calendar/feed-token');
  },

  // ---- Direct-settlement payout details (where parent fee payments are
  // sent via a Paystack subaccount) ----

  getMyPayoutDetails: async () => {
    try {
      return await api.get('/api/schools/payout-details');
    } catch (error) {
      console.error('Error fetching school payout details:', error);
      throw error;
    }
  },

  submitMyPayoutDetails: async (data) => {
    try {
      return await api.put('/api/schools/payout-details', data);
    } catch (error) {
      console.error('Error submitting school payout details:', error);
      throw error;
    }
  },

  listPendingSchoolPayoutDetails: async () => {
    try {
      return await api.get('/api/schools/payout-details/pending');
    } catch (error) {
      console.error('Error fetching pending school payout details:', error);
      throw error;
    }
  },

  getSchoolPayoutDetails: async (schoolId) => {
    try {
      return await api.get(`/api/schools/${schoolId}/payout-details`);
    } catch (error) {
      console.error('Error fetching school payout details:', error);
      throw error;
    }
  },

  verifySchoolPayoutDetails: async (schoolId) => {
    try {
      return await api.post(`/api/schools/${schoolId}/payout-details/verify`, {});
    } catch (error) {
      console.error('Error verifying school payout details:', error);
      throw error;
    }
  },

  rejectSchoolPayoutDetails: async (schoolId, reason) => {
    try {
      return await api.post(`/api/schools/${schoolId}/payout-details/reject`, { reason });
    } catch (error) {
      console.error('Error rejecting school payout details:', error);
      throw error;
    }
  },

  // ---- Canteen direct-settlement payout details (a second, independent
  // Paystack subaccount for canteen top-ups) ----

  getMyCanteenPayoutDetails: async () => {
    try {
      return await api.get('/api/schools/canteen-payout-details');
    } catch (error) {
      console.error('Error fetching canteen payout details:', error);
      throw error;
    }
  },

  submitMyCanteenPayoutDetails: async (data) => {
    try {
      return await api.put('/api/schools/canteen-payout-details', data);
    } catch (error) {
      console.error('Error submitting canteen payout details:', error);
      throw error;
    }
  },

  listPendingCanteenPayoutDetails: async () => {
    try {
      return await api.get('/api/schools/canteen-payout-details/pending');
    } catch (error) {
      console.error('Error fetching pending canteen payout details:', error);
      throw error;
    }
  },

  getSchoolCanteenPayoutDetails: async (schoolId) => {
    try {
      return await api.get(`/api/schools/${schoolId}/canteen-payout-details`);
    } catch (error) {
      console.error('Error fetching canteen payout details:', error);
      throw error;
    }
  },

  verifySchoolCanteenPayoutDetails: async (schoolId) => {
    try {
      return await api.post(`/api/schools/${schoolId}/canteen-payout-details/verify`, {});
    } catch (error) {
      console.error('Error verifying canteen payout details:', error);
      throw error;
    }
  },

  rejectSchoolCanteenPayoutDetails: async (schoolId, reason) => {
    try {
      return await api.post(`/api/schools/${schoolId}/canteen-payout-details/reject`, { reason });
    } catch (error) {
      console.error('Error rejecting canteen payout details:', error);
      throw error;
    }
  }
};

export default schoolsService;
